import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '@/lib/axios';
import type { DisenoPlantilla, Orientacion, TamanoHoja, TipoDocumento } from './template-types';

export interface PlantillaDocumentoConfig {
  diseno: DisenoPlantilla;
  tamanoHoja: TamanoHoja;
  orientacion: Orientacion;
  disenoPersonalizadoJson?: string | null;
  disenoPersonalizadoHtml?: string | null;
}

export type PlantillasConfig = Record<TipoDocumento, PlantillaDocumentoConfig>;

export interface UpdatePlantillaConfigInput {
  tipo: TipoDocumento;
  patch: Partial<PlantillaDocumentoConfig>;
}

const QUERY_KEY = ['plantillas-documento-config'];

export const DEFAULT_PLANTILLAS_CONFIG: PlantillasConfig = {
  receta:             { diseno: 'clasico',     tamanoHoja: 'a4',    orientacion: 'vertical' },
  orden:              { diseno: 'minimalista', tamanoHoja: 'a4',    orientacion: 'vertical' },
  laboratorio:        { diseno: 'clasico',     tamanoHoja: 'a4',    orientacion: 'vertical' },
  presupuesto:        { diseno: 'corporativo', tamanoHoja: 'carta', orientacion: 'vertical' },
  'plan-tratamiento': { diseno: 'clasico',     tamanoHoja: 'a4',    orientacion: 'vertical' },
  consentimiento:     { diseno: 'clasico',     tamanoHoja: 'a4',    orientacion: 'vertical' },
};

function toConfigMap(rows: Array<{ tipoDocumento: TipoDocumento } & PlantillaDocumentoConfig>): PlantillasConfig {
  const base = { ...DEFAULT_PLANTILLAS_CONFIG };
  for (const row of rows) {
    base[row.tipoDocumento] = {
      diseno: row.diseno,
      tamanoHoja: row.tamanoHoja,
      orientacion: row.orientacion,
      disenoPersonalizadoJson: row.disenoPersonalizadoJson,
      disenoPersonalizadoHtml: row.disenoPersonalizadoHtml,
    };
  }
  return base;
}

export function usePlantillasConfig() {
  return useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => api.get('/api/configuracion/plantillas-documento').then(r => toConfigMap(r.data)),
    placeholderData: DEFAULT_PLANTILLAS_CONFIG,
  });
}

export function useUpdatePlantillaConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tipo, patch }: UpdatePlantillaConfigInput) =>
      api.put(`/api/configuracion/plantillas-documento/${tipo}`, patch).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  });
}
